import React, { useState, useEffect } from 'react';
import { Calendar, ChartBar, Code, Users, Briefcase, Building, Globe, Clock, X, Plus, Loader2, Play, Check, BookOpen, Brain, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../common/Button';
import { ScheduleInterviewModal } from '../interviews/ScheduleInterviewModal';
import { InterviewCancelModal } from '../interviews/InterviewCancelModal';
import { useResponsive } from '../../contexts/ResponsiveContext';
import interviewService, { UserInterview } from '../../services/interviewService';
import { Toast, useToast } from '../common/Toast';
import { useNavigate } from 'react-router-dom';
import { DurationDisplay } from '../common/DurationDisplay';

interface UpcomingInterviewsTabProps {
  interviews: UserInterview[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}

export const UpcomingInterviewsTab: React.FC<UpcomingInterviewsTabProps> = ({
  interviews: upcomingInterviews,
  isLoading,
  error,
  onRetry
}) => {
  const { isMobile: isMobileView } = useResponsive();
  const navigate = useNavigate();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState<UserInterview | null>(null);
  const [loadingInterviewId, setLoadingInterviewId] = useState<string | null>(null);
  const [localInterviews, setLocalInterviews] = useState<UserInterview[]>(upcomingInterviews);
  const { showToast } = useToast();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Update local interviews when prop changes
  useEffect(() => {
    // Filter out interviews where interview object is null to prevent crashes
    const validInterviews = upcomingInterviews.filter(interview => 
      interview && 
      interview.interview && 
      interview.interview._id
    );
    setLocalInterviews(validInterviews);
    // Reset to first page when interviews change
    setCurrentPage(1);
  }, [upcomingInterviews]);

  // Pagination calculations
  const totalPages = Math.ceil(localInterviews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayInterviews = isLoading ? [] : localInterviews.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Smart pagination function to generate page numbers with ellipsis
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = isMobileView ? 3 : 5; // Show fewer pages on mobile
    
    if (totalPages <= maxVisiblePages + 2) {
      // If total pages is small, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      let startPage, endPage;
      
      if (currentPage <= 3) {
        // If current page is near the beginning
        startPage = 2;
        endPage = Math.min(maxVisiblePages, totalPages - 1);
      } else if (currentPage >= totalPages - 2) {
        // If current page is near the end
        startPage = Math.max(totalPages - maxVisiblePages + 1, 2);
        endPage = totalPages - 1;
      } else {
        // If current page is in the middle
        const around = Math.floor((maxVisiblePages - 1) / 2);
        startPage = Math.max(currentPage - around, 2);
        endPage = Math.min(currentPage + around, totalPages - 1);
      }
      
      // Add ellipsis before start if needed
      if (startPage > 2) {
        pages.push('ellipsis-start');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis after end if needed
      if (endPage < totalPages - 1) {
        pages.push('ellipsis-end');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Format relative time
  const formatRelativeTime = (datetimeStr: string) => {
    const datetime = new Date(datetimeStr);
    const now = new Date();
    const diffHours = Math.floor((datetime.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    if (diffHours < 24) {
      if (diffHours < 1) {
        const diffMinutes = Math.floor((datetime.getTime() - now.getTime()) / (1000 * 60));
        if (diffMinutes < 1) {
          return 'Now';
        } else {
          return `${diffMinutes} minutes from now`;
        }
      } else {
        return `${diffHours} hours from now`;
      }
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} days from now`;
    }
  };

  // Check if interview is joinable (current time is between scheduledAt and 10 minutes after or between scheduledAt and 5 minutes before)
  const isInterviewJoinable = (scheduledAt: string) => {
    const scheduledTime = new Date(scheduledAt).getTime();
    const currentTime = new Date().getTime();
    const tenMinutesInFutureMs = 10 * 60 * 1000;
    const fiveMinutesInPastMs = -5 * 60 * 1000;
    
    const isInFuture = currentTime >= scheduledTime && currentTime <= scheduledTime + tenMinutesInFutureMs; // 10 minutes after scheduledAt
    const isInPast = currentTime >= scheduledTime + fiveMinutesInPastMs && currentTime <= scheduledTime; // 5 minutes before scheduledAt
    return isInFuture || isInPast;
  };

  const handleReschedule = (interview: UserInterview) => {
    setSelectedInterview(interview);
    setShowScheduleModal(true);
  };
  
  const handleCancel = (interview: UserInterview) => {
    setSelectedInterview(interview);
    setShowCancelModal(true);
  };
  
  const handleJoinInterview = async (interview: UserInterview) => {
    setLoadingInterviewId(interview._id);
    try {
      // Navigate to interview preview page with interview details
      navigate(`/interview-preview/${interview._id}`, {
        state: {
          interviewDetails: {
            scheduledInterviewId: interview._id, // This is the userInterviewId which means user's scheduled interview id
            id: interview.interview?._id,
            title: interview.interview?.title,
            company: interview.interview?.company,
            companyLogo: interview.interview?.companyLogo,
            role: interview.interview?.role,
            duration: interview.interview?.durationMinutes,
            description: interview.interview?.description,
            language: interview.interview?.language,
            newInterview: false,
            tags: interview.interview?.tags,
            status: "scheduled",
            voicePack: interview.interview?.voicePack,
            characterGender: interview.interview?.characterGender,
            interviewPrompt: interview.interview?.interviewPrompt,
            createdAt: interview.interview?.createdAt,
            updatedAt: interview.interview?.updatedAt,
            series: interview.series
          }
        }
      });
    } catch (err) {
      console.error('Error joining interview:', err);
      showToast('Failed to join interview. Please try again.', 'error');
      setLoadingInterviewId(null);
    }
  };
  
  const handleRescheduleSubmit = async (date: Date, language?: string) => {
    if (!selectedInterview) return;
    
    setLoadingInterviewId(selectedInterview._id);
    try {
      // Call the API to reschedule the interview
      await interviewService.rescheduleInterview(
        selectedInterview._id,
        date,
        language
      );
      
      // Update the local state with rescheduled interview
      const updatedInterviews = localInterviews.map(interview => {
        if (interview._id === selectedInterview._id) {
          return {
            ...interview,
            scheduledAt: date.toISOString(),
            ...(interview.interview && {
              interview: {
                ...interview.interview,
                language: language || interview.interview.language
              }
            })
          } as UserInterview;
        }
        return interview;
      });
      
      setLocalInterviews(updatedInterviews);
      
      // Call onRetry to refresh the list of interviews from the server
      onRetry();
      
      setShowScheduleModal(false);
      showToast('Interview rescheduled successfully', 'success');
    } catch (err) {
      console.error('Error rescheduling interview:', err);
      showToast('Failed to reschedule interview. Please try again.', 'error');
    } finally {
      setLoadingInterviewId(null);
    }
  };
  

  // Add the actual cancellation API call function
  const handleCancelInterview = async () => {
    if (!selectedInterview || !selectedInterview._id) {
      showToast('Cannot cancel interview: missing interview ID', 'error');
      return;
    }

    setLoadingInterviewId(selectedInterview._id);
    try {
      // Cancel the interview using the service method
      await interviewService.cancelInterview(selectedInterview._id);
      
      // Update the local state immediately by removing the cancelled interview
      const updatedInterviews = localInterviews.filter(
        interview => interview._id !== selectedInterview._id
      );
      setLocalInterviews(updatedInterviews);
      
      // Show success message
      showToast('Interview cancelled successfully', 'success');
      
      // Close the modal
      setShowCancelModal(false);
      
      // Call onRetry to refresh the list of interviews from the server
      onRetry();
    } catch (error) {
      console.error('Error cancelling interview:', error);
      showToast('Failed to cancel interview. Please try again.', 'error');
    } finally {
      setLoadingInterviewId(null);
    }
  };

  // Map interview types to icons
  const getInterviewTypeIcon = (tags: string[] = []) => {
    const lowerTags = tags.map(tag => tag.toLowerCase());
    
    if (lowerTags.includes('system design')) {
      return <ChartBar size={22} className="text-primary-light dark:text-primary-dark" />;
    } else if (lowerTags.includes('coding')) {
      return <Code size={22} className="text-primary-light dark:text-primary-dark" />;
    } else {
      return <Users size={22} className="text-primary-light dark:text-primary-dark" />;
    }
  };

  return (
    <div className="bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
      <div className="flex items-center mb-6">
        {!isMobileView && (
          <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-3">
            <Calendar className="w-5 h-5 text-primary-light dark:text-primary-dark" />
          </div>
        )}
        <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">
          Upcoming Interviews
        </h3>
      </div>
      
      <div className="space-y-6">
        {isLoading ? (
          <div className="text-center py-8">
            <Loader2 size={32} className="animate-spin mx-auto text-primary-light dark:text-primary-dark mb-2" />
            <p className="text-base text-text-secondary-light dark:text-text-secondary-dark">
              Loading your upcoming interviews...
            </p>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">
            <p>{error}</p>
            <Button 
              variant="outline" 
              size="sm"
              className="mt-4"
              onClick={onRetry}
            >
              Retry
            </Button>
          </div>
        ) : displayInterviews.length > 0 ? (
          <>
            {displayInterviews.map(interview => (
              <div key={interview._id} className="p-4 md:p-5 border rounded-lg border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark hover:border-primary-light/50 dark:hover:border-primary-dark/50 transition-all duration-300">
                <div className="flex flex-col">
                  <div className={`flex items-start ${isMobileView ? 'flex-col' : ''}`}>
                    {!isMobileView && (
                      <div className="mr-5 bg-primary-light/10 dark:bg-primary-dark/10 p-3 rounded-full">
                        {getInterviewTypeIcon(interview.interview?.tags)}
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <h3 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-1">
                        {interview.interview?.title || 'Interview'} - {interview.interview?.role || 'Position'}
                      </h3>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                        {interview.interview?.jobDescription ? 
                          interview.interview.jobDescription.substring(0, 90) + '...' : 
                          interview.interview?.description ? 
                            interview.interview.description.substring(0, 90) + '...' :
                            'Interview description'
                        }
                      </p>
                    </div>
                    
                    <div className={`flex items-center text-xs text-text-tertiary-light dark:text-text-tertiary-dark rounded-2xl bg-background-light/50 dark:bg-background-dark/50 px-3 py-2 whitespace-nowrap ${isMobileView ? 'self-start mt-2 mb-2' : ''}`}>
                      <Calendar size={12} className="mr-1" />
                      <span>{isInterviewJoinable(interview.scheduledAt) ? "Now" : formatRelativeTime(interview.scheduledAt)}</span>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 mt-4 mb-4">
                    <span className="inline-flex items-center text-xs bg-green-500/5 dark:bg-green-400/5 px-2 py-1 rounded-full border border-green-600/10 dark:border-green-400/10">
                      <Briefcase size={13} className="mr-1 text-green-600 dark:text-green-400" />
                      <span className="text-green-600 dark:text-green-400">{interview.interview?.role || 'Position'}</span>
                    </span>
                    {interview.series && (
                      <span className="inline-flex items-center text-xs bg-purple-500/5 dark:bg-purple-400/5 px-2 py-1 rounded-full border border-purple-600/10 dark:border-purple-400/10">
                        <BookOpen size={13} className="mr-1 text-purple-600 dark:text-purple-400" />
                        <span className="text-purple-600 dark:text-purple-400">{interview.series.name.length > 25 ? interview.series.name.slice(0, 25) + '...' : interview.series.name}</span>
                      </span>
                    )}
                    <span className="inline-flex items-center text-xs bg-purple-500/5 dark:bg-purple-400/5 px-2 py-1 rounded-full border border-purple-500/10 dark:border-purple-400/10">
                      <Building size={13} className="mr-1 text-purple-500 dark:text-purple-400" />
                      <span className="text-purple-500 dark:text-purple-400">{interview.interview?.company || 'Company'}</span>
                    </span>
                    <span className="inline-flex items-center text-xs bg-green-500/5 dark:bg-green-400/5 px-2 py-1 rounded-full border border-green-500/10 dark:border-green-400/10">
                      <Globe size={13} className="mr-1 text-green-500 dark:text-green-400" />
                      <span className="text-text-primary-light dark:text-text-primary-dark">{interview.languageName || 'English'}</span>
                    </span>
                    {/* <DurationDisplay 
                      minutes={interview.interview?.durationMinutes || 30}
                      clockSize={13}
                    /> */}
                    <div className={`inline-flex items-center text-xs ${(interview.interview?.difficulty ?? 0) <= 2 ? "bg-green-600/15 dark:bg-green-400/15" :
                        (interview.interview?.difficulty ?? 0) <= 4 ? "bg-amber-600/15 dark:bg-amber-400/15" :
                          "bg-red-600/15 dark:bg-red-400/15"
                      } px-2 py-1 rounded-full border ${(interview.interview?.difficulty ?? 0) <= 2 ? "border-green-600/20 dark:border-green-400/20" :
                        (interview.interview?.difficulty ?? 0) <= 4 ? "border-amber-600/20 dark:border-amber-400/20" :
                          "border-red-600/20 dark:border-red-400/20"
                      }`}>
                      <Brain size={13} className={`mr-1 ${(interview.interview?.difficulty ?? 0) <= 2 ? "text-green-600 dark:text-green-400" :
                          (interview.interview?.difficulty ?? 0) <= 4 ? "text-amber-600 dark:text-amber-400" :
                            "text-red-600 dark:text-red-400"
                        }`} />
                      <span className={`mr-1 ${(interview.interview?.difficulty ?? 0) <= 2 ? "text-green-600 dark:text-green-400" :
                          (interview.interview?.difficulty ?? 0) <= 4 ? "text-amber-600 dark:text-amber-400" :
                            "text-red-600 dark:text-red-400"
                        }`}>Difficulty:</span>
                      <span className={`font-medium ${(interview.interview?.difficulty ?? 0) <= 2 ? "text-green-600 dark:text-green-400" :
                          (interview.interview?.difficulty ?? 0) <= 4 ? "text-amber-600 dark:text-amber-400" :
                            "text-red-600 dark:text-red-400"
                        }`}>
                        {(interview.interview?.difficulty ?? 0) <= 2 ? "Easy" :
                          (interview.interview?.difficulty ?? 0) <= 4 ? "Medium" :
                            "Hard"}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-2 pt-3 border-t border-border-light/30 dark:border-border-dark/30">
                    <div className="mb-3">
                      <div className="flex items-center mb-1">
                        <Calendar size={15} className="text-primary-light dark:text-primary-dark mr-2" />
                        <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                          {new Date(interview.scheduledAt).toLocaleString(undefined, {
                            weekday: 'long',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {isInterviewJoinable(interview.scheduledAt) ? (
                        <Button 
                          variant="primary" 
                          size="sm"
                          leftIcon={<Play size={14} className="text-white" />}
                          onClick={() => handleJoinInterview(interview)}
                          disabled={loadingInterviewId === interview._id}
                          loading={loadingInterviewId === interview._id}
                        >
                          {loadingInterviewId === interview._id ? 'Joining...' : 'Join Interview'}
                        </Button>
                      ) : (
                        <Button 
                          variant="secondary" 
                          size="sm"
                          className='py-3 px-5'
                          leftIcon={<Calendar size={14} />}
                          onClick={() => handleReschedule(interview)}
                          disabled={loadingInterviewId === interview._id}
                          loading={loadingInterviewId === interview._id && showScheduleModal}
                        >
                          {loadingInterviewId === interview._id && showScheduleModal ? 'Rescheduling...' : 'Reschedule'}
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        className='py-3 px-5'
                        leftIcon={<X size={14} />}
                        onClick={() => handleCancel(interview)}
                        disabled={loadingInterviewId === interview._id}
                        loading={loadingInterviewId === interview._id && showCancelModal}
                      >
                        {loadingInterviewId === interview._id && showCancelModal ? 'Cancelling...' : 'Cancel'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-6 pt-4 border-t border-border-light dark:border-border-dark">
                {/* Mobile Layout */}
                {isMobileView ? (
                  <div className="space-y-3">
                    {/* Page info - centered on mobile */}
                    <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark text-center">
                      Page {currentPage} of {totalPages}
                    </div>
                    
                    {/* Page numbers row */}
                    <div className="flex items-center justify-center gap-1 overflow-x-auto pb-1">
                      {generatePageNumbers().map((page, index) => {
                        if (typeof page === 'string') {
                          // Handle ellipsis
                          return (
                            <span
                              key={page}
                              className="px-2 py-1 text-text-secondary-light dark:text-text-secondary-dark text-sm flex-shrink-0"
                            >
                              ...
                            </span>
                          );
                        }
                        
                        // Handle regular page numbers
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "primary" : "outline"}
                            size="sm"
                            className={`px-2 py-1 text-sm min-w-[32px] flex-shrink-0 ${currentPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Button>
                        );
                      })}
                    </div>
                    
                    {/* Navigation buttons row */}
                    <div className="flex items-center justify-between gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<ChevronLeft size={14} />}
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        className="flex-1"
                      >
                        Prev
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        rightIcon={<ChevronRight size={14} />}
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        className="flex-1"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Desktop Layout - Original row design */
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                      Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<ChevronLeft size={14} />}
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      >
                        Previous
                      </Button>
                      
                      {/* Page numbers */}
                      <div className="flex items-center gap-1">
                        {generatePageNumbers().map((page, index) => {
                          if (typeof page === 'string') {
                            // Handle ellipsis
                            return (
                              <span
                                key={page}
                                className="px-3 py-2 text-text-secondary-light dark:text-text-secondary-dark text-sm"
                              >
                                ...
                              </span>
                            );
                          }
                          
                          // Handle regular page numbers
                          return (
                            <Button
                              key={page}
                              variant={currentPage === page ? "primary" : "outline"}
                              size="sm"
                              className={`px-3 ${currentPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        rightIcon={<ChevronRight size={14} />}
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          localInterviews.length === 0 && (
            <div className="text-center py-8 text-text-secondary-light dark:text-text-secondary-dark">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-surface-light dark:bg-surface-dark mb-4">
                <Calendar size={32} className="text-text-secondary-light dark:text-text-secondary-dark" />
              </div>
              <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark mb-2 font-heading font-semibold">
                No upcoming interviews
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
                Schedule your next interview to continue improving
              </p>
            </div>
          )
        )}
      </div>

      {showScheduleModal && selectedInterview && (
        <ScheduleInterviewModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          interviewDetails={{
            id: selectedInterview._id,
            title: selectedInterview.interview?.title || '',
            company: selectedInterview.interview?.company || '',
            jobRole: selectedInterview.interview?.role || '',
            duration: selectedInterview.interview?.durationMinutes || 30,
            description: selectedInterview.interview?.description || ''
          }}
          initialDate={selectedInterview.scheduledAt}
          mode="reschedule"
          onSchedule={handleRescheduleSubmit}
        />
      )}
      
      {showCancelModal && selectedInterview && (
        <InterviewCancelModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onCancel={handleCancelInterview}
          interviewDetails={{
            id: selectedInterview._id,
            title: selectedInterview.interview?.title || '',
            company: selectedInterview.interview?.company || '',
            jobRole: selectedInterview.interview?.role || '',
            date: selectedInterview.scheduledAt,
            duration: selectedInterview.interview?.durationMinutes || 30,
            description: selectedInterview.interview?.description || '',
            language: selectedInterview.interview?.language || 'English',
            score: 0
          }}
          isLoading={loadingInterviewId === selectedInterview._id && showCancelModal}
        />
      )}
    </div>
  );
};